import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useOverviewNumbers } from 'dashboard/hooks/useOverviewNumbers';
import { useOverviewUrgency } from 'dashboard/hooks/useOverviewUrgency';
import { useOverviewRisks } from 'dashboard/hooks/useOverviewRisks';
import { useInsightsEvents } from 'dashboard/hooks/useInsightsEvents';
import { usePosthogCapture } from 'dashboard/posthogCapture/_index';
import { SelectionDropdown } from 'shared/components/dropdown/SelectionDropdown';
import routesConfig from 'constants/routesConfig.json';

import * as Styled from 'dashboard/styles/Overview.style';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import NumbersCard from './NumbersCard';
import UrgencyCard from './UrgencyCard';
import RisksCard from './RisksCard';
import SummaryCard from './SummaryCard';

type OverviewView = 'numbers' | 'urgency' | 'risks';

interface OverviewSectionProps {
  queryParams: string;
}

export default function OverviewSection({
  queryParams,
}: OverviewSectionProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');
  const dashboard = usePosthogCapture();
  const [selectedView, setSelectedView] = useState<OverviewView>('numbers');

  const { data: numbersData, isLoading: numbersLoading } =
    useOverviewNumbers(queryParams);
  const { data: urgencyData, isLoading: urgencyLoading } =
    useOverviewUrgency(queryParams);
  const { data: risksData, isLoading: risksLoading } =
    useOverviewRisks(queryParams);
  const { data: eventsData, isLoading: eventsLoading } =
    useInsightsEvents(queryParams);

  const eventsCount = eventsData?.pages[0]?.count;

  const scrollToEvents = useCallback(() => {
    document
      .getElementById('events-section')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const viewOptions = [
    { value: 'numbers', label: t('overview.title') },
    { value: 'urgency', label: t('overview.urgency') },
    { value: 'risks', label: t('overview.mostCommonRisks') },
  ];

  const handleViewChange = (value: string) => {
    const view = value as OverviewView;
    setSelectedView(view);
    dashboard.overviewViewSelected({ view });
  };

  return (
    <Styled.SectionContainer>
      <Styled.SectionHeader>
        <Text.H5>{t('overview.title')}</Text.H5>
        <Buttons.SecondaryLight
          as={Link}
          to={`${routesConfig.INSIGHTS_PAGE}?view=summaries`}
          $size="small"
          onClick={() => dashboard.shiftSummariesClicked()}
        >
          {t('viewShiftSummaries')}
        </Buttons.SecondaryLight>
      </Styled.SectionHeader>

      <Styled.OverviewLayout>
        <SummaryCard queryParams={queryParams} />

        <Styled.Card>
          <Styled.CardHeader>
            <SelectionDropdown
              ariaLabel={t('overview.selectView')}
              value={selectedView}
              options={viewOptions}
              onChange={handleViewChange}
            />
          </Styled.CardHeader>

          {selectedView === 'numbers' && (
            <NumbersCard
              data={numbersData}
              eventsCount={eventsCount}
              isLoading={numbersLoading || eventsLoading}
              onEventsClick={scrollToEvents}
            />
          )}
          {selectedView === 'urgency' && (
            <UrgencyCard data={urgencyData} isLoading={urgencyLoading} />
          )}
          {selectedView === 'risks' && (
            <RisksCard data={risksData} isLoading={risksLoading} />
          )}
        </Styled.Card>
      </Styled.OverviewLayout>
    </Styled.SectionContainer>
  );
}
